/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'

import type { EncryptedData, EncryptionState, CustomerKeyPair, CustomerWrappedDEK } from '@/types/crypto'
import { customerCryptoService } from '@/services/customerCrypto.service'
import { eciesService } from '@/services/ecies.service'
import { cryptoService } from '@/services/crypto.service'
import { customerDataService, type EncryptedRecord } from '@/services/customerData.service'

/**
 * Encryption Context for Customer App
 *
 * Manages the customer's encryption state including:
 * - Passphrase-based key derivation (Argon2id)
 * - X25519 key pair generation
 * - DEK unwrapping via authenticated ECIES
 * - Data decryption using the DEK
 *
 * Security Architecture:
 * 1. Customer enters passphrase
 * 2. Argon2id derives a seed from passphrase + stored salt
 * 3. X25519 key pair is generated from seed
 * 4. Wrapped DEK is fetched from backend
 * 5. DEK is unwrapped using authenticated ECIES
 * 6. DEK is stored in memory for decryption
 * 7. On lock/logout, DEK and private key are cleared
 *
 * NOTE: This is READ-ONLY - customers can only decrypt, not encrypt.
 */

// ============================================================================
// TYPES
// ============================================================================

interface EncryptionContextType {
  /** Current encryption state */
  state: EncryptionState

  /** Whether the encryption layer is unlocked (DEK available) */
  isUnlocked: boolean

  /** Whether the encryption layer is currently loading */
  isLoading: boolean

  /** Error message if unlock failed */
  error: string | null

  /** Current DEK version (for rotation support) */
  dekVersion: number | null

  /**
   * The raw DEK bytes for document decryption
   *
   * Only available when unlocked. Use this for decrypting binary files
   * (documents, images) where the standard decrypt() function doesn't apply.
   *
   * WARNING: Handle with care - this is sensitive cryptographic material.
   */
  dek: Uint8Array | null

  /**
   * Unlock the encryption layer with customer's passphrase
   *
   * Flow:
   * 1. Fetch wrapped DEK and salt from backend
   * 2. Derive key pair from passphrase + salt
   * 3. Unwrap DEK using authenticated ECIES
   * 4. Store DEK in memory
   *
   * @param passphrase - Customer's passphrase
   * @throws Error if passphrase is wrong or DEK unwrapping fails
   */
  unlock: (passphrase: string) => Promise<void>

  /**
   * Lock the encryption layer
   *
   * Clears DEK and private key from memory.
   * Should be called on logout or after inactivity timeout.
   */
  lock: () => void

  /**
   * Decrypt a single value
   *
   * @param encryptedData - Encrypted data (ciphertext + IV)
   * @returns Decrypted value
   */
  decrypt: <T>(encryptedData: EncryptedData) => Promise<T>

  /**
   * Decrypt a record (encrypted data + metadata)
   *
   * @param record - Encrypted record from API
   * @returns Decrypted data
   */
  decryptRecord: <T>(record: EncryptedRecord) => Promise<T>

  /**
   * Decrypt multiple records
   *
   * @param records - Array of encrypted records
   * @returns Array of decrypted data with metadata
   */
  decryptRecords: <T>(
    records: EncryptedRecord[]
  ) => Promise<Array<T & { _id: string; _metadata?: EncryptedRecord['metadata'] }>>

  /**
   * Safely decrypt (returns null on failure instead of throwing)
   *
   * @param encryptedData - Encrypted data (may be null/undefined)
   * @returns Decrypted value or null
   */
  safeDecrypt: <T>(encryptedData: EncryptedData | null | undefined) => Promise<T | null>
}

// ============================================================================
// CONTEXT
// ============================================================================

const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined)

/**
 * Type for the fetchWrappedDEK function
 */
type FetchWrappedDEKFn = () => Promise<{
  wrappedDEK: CustomerWrappedDEK
  salt: string // Base64-encoded salt
  version: number
}>

interface EncryptionProviderProps {
  children: ReactNode
  /**
   * Function to fetch the wrapped DEK from the backend.
   * This should return the ECIES-wrapped DEK and the customer's salt.
   * If not provided, a placeholder implementation is used.
   */
  fetchWrappedDEK?: FetchWrappedDEKFn
}

/**
 * Placeholder fetchWrappedDEK for development
 * This will throw an error when called - real implementation should be provided
 */
const placeholderFetchWrappedDEK: FetchWrappedDEKFn = async () => {
  throw new Error(
    'fetchWrappedDEK not configured. ' +
      'Please provide a fetchWrappedDEK function to EncryptionProvider ' +
      'or implement the backend /auth/dek endpoint.'
  )
}

export function EncryptionProvider({ children, fetchWrappedDEK = placeholderFetchWrappedDEK }: EncryptionProviderProps) {
  // Encryption state
  const [state, setState] = useState<EncryptionState>({
    isUnlocked: false,
    isLoading: false,
    error: null,
    dekVersion: null,
  })

  // DEK stored in memory only (never persisted)
  const [dek, setDek] = useState<Uint8Array | null>(null)

  // Customer key pair stored in memory only
  const [keyPair, setKeyPair] = useState<CustomerKeyPair | null>(null)

  /**
   * Unlock the encryption layer with passphrase
   */
  const unlock = useCallback(
    async (passphrase: string) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        // 1. Fetch wrapped DEK and salt from backend
        const { wrappedDEK, salt, version } = await fetchWrappedDEK()

        // 2. Decode salt from Base64
        const saltBytes = Uint8Array.from(atob(salt), (c) => c.charCodeAt(0))

        // 3. Derive key pair from passphrase + salt
        const { keyPair: derivedKeyPair } = customerCryptoService.deriveKeyPairFromPassphrase(passphrase, saltBytes)

        // 4. Unwrap DEK using authenticated ECIES
        // This will fail if:
        // - Passphrase is wrong (different key pair)
        // - Data was tampered with
        // - Consultant identity doesn't match (MITM protection)
        const unwrappedDEK = await eciesService.unwrapDEK(wrappedDEK, derivedKeyPair)

        // 5. Store DEK and key pair in memory
        setDek(unwrappedDEK)
        setKeyPair(derivedKeyPair)
        setState({
          isUnlocked: true,
          isLoading: false,
          error: null,
          dekVersion: version,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to unlock encryption'

        // Clear any partial state
        setDek(null)
        setKeyPair(null)
        setState({
          isUnlocked: false,
          isLoading: false,
          error: message,
          dekVersion: null,
        })

        throw error
      }
    },
    [fetchWrappedDEK]
  )

  /**
   * Lock the encryption layer
   */
  const lock = useCallback(() => {
    // Securely clear DEK and key pair
    if (dek) {
      customerCryptoService.secureClear(dek)
    }
    if (keyPair) {
      customerCryptoService.secureClear(keyPair.privateKey)
    }

    setDek(null)
    setKeyPair(null)
    setState({
      isUnlocked: false,
      isLoading: false,
      error: null,
      dekVersion: null,
    })
  }, [dek, keyPair])

  /**
   * Decrypt a single value
   */
  const decrypt = useCallback(
    async <T,>(encryptedData: EncryptedData): Promise<T> => {
      if (!dek) {
        throw new Error('Encryption layer is locked. Please unlock first.')
      }
      return cryptoService.decrypt<T>(encryptedData, dek)
    },
    [dek]
  )

  /**
   * Decrypt a record
   */
  const decryptRecord = useCallback(
    async <T,>(record: EncryptedRecord): Promise<T> => {
      if (!dek) {
        throw new Error('Encryption layer is locked. Please unlock first.')
      }
      return customerDataService.decryptRecord<T>(record, dek)
    },
    [dek]
  )

  /**
   * Decrypt multiple records
   */
  const decryptRecords = useCallback(
    async <T,>(
      records: EncryptedRecord[]
    ): Promise<Array<T & { _id: string; _metadata?: EncryptedRecord['metadata'] }>> => {
      if (!dek) {
        throw new Error('Encryption layer is locked. Please unlock first.')
      }
      return customerDataService.decryptRecords<T>(records, dek)
    },
    [dek]
  )

  /**
   * Safely decrypt (returns null on failure)
   */
  const safeDecrypt = useCallback(
    async <T,>(encryptedData: EncryptedData | null | undefined): Promise<T | null> => {
      if (!dek || !encryptedData) {
        return null
      }
      return customerDataService.safeDecrypt<T>(encryptedData, dek)
    },
    [dek]
  )

  // Memoize context value
  const value = useMemo<EncryptionContextType>(
    () => ({
      state,
      isUnlocked: state.isUnlocked,
      isLoading: state.isLoading,
      error: state.error,
      dekVersion: state.dekVersion,
      dek,
      unlock,
      lock,
      decrypt,
      decryptRecord,
      decryptRecords,
      safeDecrypt,
    }),
    [state, dek, unlock, lock, decrypt, decryptRecord, decryptRecords, safeDecrypt]
  )

  return <EncryptionContext.Provider value={value}>{children}</EncryptionContext.Provider>
}

/**
 * Hook to access encryption context
 */
export function useEncryption(): EncryptionContextType {
  const context = useContext(EncryptionContext)
  if (context === undefined) {
    throw new Error('useEncryption must be used within an EncryptionProvider')
  }
  return context
}

/**
 * Hook to get just the unlock status (for conditional rendering)
 */
export function useEncryptionStatus(): { isUnlocked: boolean; isLoading: boolean; error: string | null } {
  const { isUnlocked, isLoading, error } = useEncryption()
  return { isUnlocked, isLoading, error }
}

export default EncryptionContext
